// Save / copy / region capture, including the drag marquee.
import type { App } from '../app.ts';
import { $ } from './dom.ts';
import { copyCanvas, downloadCanvas, feasibleScale, renderRegion, safeName, stamp } from '../export/png.ts';
import { strideFor } from '../render/loop.ts';

export interface Capture {
  picking: boolean;
  setPicking(on: boolean): void;
  captureWholeView(): Promise<void>;
  copyWholeView(): Promise<void>;
  /** Marquee handlers used by the pointer module. */
  beginMark(x: number, y: number): void;
  moveMark(x: number, y: number): void;
  endMark(x: number, y: number): Promise<void>;
  readonly marking: boolean;
}

export function mountCapture(app: App): Capture {
  const stage = $('mainStage');
  const marquee = $('marquee');
  const marqueeSize = $('marqueeSize');
  const pickBanner = $('pickBanner');
  const btnShotRegion = $('btnShotRegion');
  let picking = false;
  let marking = false;
  let markStart: [number, number] = [0, 0];

  const wantScale = () => (app.state.shot.scale === 'max' ? Infinity : app.state.shot.scale);
  const exportParams = () => {
    const n = app.geom?.nPoints ?? 0;
    const stride = app.state.shot.fullDetail ? 1 : strideFor(app.state.detail, n, app.renderer.budget, false);
    return { ...app.frameParams(stride), transparent: app.state.shot.transparent };
  };
  const fileName = (tag: string) =>
    [safeName(app.state.curve), safeName(app.exp.label), tag, stamp()].filter(Boolean).join('_') + '.png';

  const setPicking = (on: boolean) => {
    picking = on;
    stage.classList.toggle('picking', on);
    pickBanner.style.display = on ? 'block' : 'none';
    btnShotRegion.classList.toggle('armed', on);
    btnShotRegion.textContent = on ? 'Cancel region' : 'Save a region';
    if (!on) { marking = false; marquee.style.display = 'none'; }
  };

  const updateMarquee = (x0: number, y0: number, x1: number, y1: number) => {
    const rx = Math.min(x0, x1), ry = Math.min(y0, y1);
    const rw = Math.abs(x1 - x0), rh = Math.abs(y1 - y0);
    marquee.style.display = 'block';
    marquee.style.left = rx + 'px';
    marquee.style.top = ry + 'px';
    marquee.style.width = rw + 'px';
    marquee.style.height = rh + 'px';
    const used = Math.min(wantScale(), feasibleScale(Math.max(2, rw), Math.max(2, rh)));
    marqueeSize.textContent = Math.round(rw * used) + ' × ' + Math.round(rh * used) + ' px';
  };

  const save = async (x0: number, y0: number, x1: number, y1: number, tag: string) => {
    if (!app.geom) { app.toast('Nothing to save yet'); return; }
    const res = await renderRegion(app.renderer, exportParams(), x0, y0, x1, y1, wantScale());
    if (!res) { app.toast(tag === 'region' ? 'Region too small' : 'Nothing to save'); return; }
    app.toast('Rendering ' + res.W + ' × ' + res.H + (res.clamped ? ' (clamped to browser limit)' : '') + '...', 1600);
    const name = fileName(tag);
    if (await downloadCanvas(res.canvas, name)) app.toast('Saved ' + name);
    else app.toast('Could not encode the image');
  };

  const cap: Capture = {
    get picking() { return picking; },
    get marking() { return marking; },
    setPicking,
    captureWholeView: () => save(0, 0, app.viewW, app.viewH, 'view'),
    async copyWholeView() {
      if (!app.geom) { app.toast('Nothing to copy yet'); return; }
      const res = await renderRegion(app.renderer, exportParams(), 0, 0, app.viewW, app.viewH, wantScale());
      if (!res) { app.toast('Could not build the image'); return; }
      if (await copyCanvas(res.canvas)) app.toast('Copied to clipboard');
      else {
        app.toast('Clipboard blocked here - saving a file instead');
        await downloadCanvas(res.canvas, fileName('view'));
      }
    },
    beginMark(x, y) { marking = true; markStart = [x, y]; updateMarquee(x, y, x, y); },
    moveMark(x, y) { if (marking) updateMarquee(markStart[0], markStart[1], x, y); },
    async endMark(x, y) {
      if (!marking) return;
      marking = false;
      marquee.style.display = 'none';
      setPicking(false);
      await save(markStart[0], markStart[1], x, y, 'region');
    },
  };

  $('btnShotAll').addEventListener('click', () => void cap.captureWholeView());
  $('btnCopy').addEventListener('click', () => void cap.copyWholeView());
  btnShotRegion.addEventListener('click', () => setPicking(!picking));
  return cap;
}
